import axios from "axios";

/**
 * API base URL.
 *
 * On Emergent, BOTH the preview domain and any linked custom domain
 * (e.g. moovbytes.com) route `/api/*` to the backend via the same
 * ingress, and `REACT_APP_BACKEND_URL` is baked in pointing at that
 * same origin — so preferring it here is a no-op there.
 *
 * When the backend is deployed on a genuinely different origin (e.g.
 * separate app./api. subdomains), `REACT_APP_BACKEND_URL` is the only
 * correct source of truth; falling back to `window.location.origin`
 * would silently point every API/WS call at the frontend's own origin
 * instead. That cross-origin case relies on the backend's CORS_ORIGINS
 * + SameSite=None cookie config (see backend/auth.py) rather than the
 * same-origin trick described above.
 */
export function apiBaseUrl() {
    const configured = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    if (configured) return configured;
    if (typeof window !== "undefined" && window.location?.origin) {
        return window.location.origin;
    }
    return "";
}

export const API = `${apiBaseUrl()}/api`;

export const http = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

export function formatApiErrorDetail(detail) {
    if (detail == null) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    // Backend's PlanLimitExceeded (plan_limits.py) and a few other
    // structured 4xx errors use `.message`, not FastAPI's default `.msg`
    // -- without this branch every plan-cap/device-limit error rendered
    // as the literal string "[object Object]" instead of the actual
    // "Activation limit reached..." text the backend already wrote.
    if (detail && typeof detail.message === "string") return detail.message;
    return String(detail);
}

export function errMsg(e) {
    const detail = formatApiErrorDetail(e?.response?.data?.detail);
    if (detail) return detail;
    const status = e?.response?.status;
    if (status) return `Request failed (HTTP ${status})`;
    return e?.message || "Something went wrong. Please try again.";
}

export function formatBytes(n) {
    if (!n || n <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB", "PB"];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Split a byte count into a numeric value + unit for the metric/unit UI. */
export function splitBytes(n) {
    if (!n || n <= 0) return { value: 0, unit: "B", display: "0" };
    const units = ["B", "KB", "MB", "GB", "TB", "PB"];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    const decimals = v >= 100 || i === 0 ? 0 : v >= 10 ? 1 : 2;
    return { value: Number(v.toFixed(decimals)), unit: units[i], display: v.toFixed(decimals) };
}

/** Split an MB/s speed into a numeric value + unit, auto-scaling MB/s → GB/s. */
export function splitSpeedMbps(mbps) {
    if (!mbps || mbps <= 0) return { value: 0, unit: "MB/s", display: "0" };
    if (mbps >= 1024) {
        const gbps = mbps / 1024;
        const d = gbps >= 100 ? 0 : gbps >= 10 ? 1 : 2;
        return { value: Number(gbps.toFixed(d)), unit: "GB/s", display: gbps.toFixed(d) };
    }
    const d = mbps >= 100 ? 0 : mbps >= 10 ? 1 : 1;
    return { value: Number(mbps.toFixed(d)), unit: "MB/s", display: mbps.toFixed(d) };
}

/** Split a percentage (0-100) into a numeric + "%" unit. */
export function splitPct(pct) {
    const v = Number.isFinite(pct) ? pct : 0;
    const d = v >= 10 ? 0 : 1;
    return { value: Number(v.toFixed(d)), unit: "%", display: v.toFixed(d) };
}

export function formatDuration(sec) {
    if (!sec || sec < 0) return "—";
    if (sec < 60) return `${sec.toFixed(0)}s`;
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    if (m < 60) return `${m}m ${s}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
}

export function formatPct(p) {
    return `${(p * 100).toFixed(1)}%`;
}

export function shortId(id) {
    if (!id) return "—";
    return id.slice(0, 8);
}
